import { API_BASE_URL } from "@/api/forms";

type VoiceProvider = "openai" | "google";

export interface VoiceTranscriptionResponse {
  transcript: string;
  provider: VoiceProvider;
  durationMs: number;
  chargedCredit: boolean;
}

export interface TranscribeVoiceInput {
  projectId: string;
  formId: string;
  version: number;
  sessionToken: string;
  audio: Blob;
  locale?: string;
  questionRef?: string;
  attempt?: number;
  durationMs?: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const unwrapApiResponse = <T>(payload: unknown): T | undefined => {
  if (!isRecord(payload)) {
    return undefined;
  }
  if (payload.success === true && "data" in payload) {
    return payload.data as T;
  }
  return undefined;
};

const extractErrorMessage = (payload: unknown): string | undefined => {
  if (!isRecord(payload)) {
    return undefined;
  }
  const error = payload.error;
  if (typeof error === "string") {
    return error;
  }
  if (isRecord(error) && typeof error.message === "string") {
    return error.message;
  }
  if (typeof payload.message === "string") {
    return payload.message;
  }
  return undefined;
};

const extractErrorCode = (payload: unknown): string | undefined => {
  if (!isRecord(payload)) {
    return undefined;
  }
  const error = payload.error;
  if (isRecord(error) && typeof error.code === "string") {
    return error.code;
  }
  return undefined;
};

export class VoiceTranscriptionError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = "VoiceTranscriptionError";
  }
}

export async function transcribeVoice(
  input: TranscribeVoiceInput,
): Promise<VoiceTranscriptionResponse> {
  const formData = new FormData();
  formData.append("audio", input.audio, "voice-input.webm");

  if (input.locale) {
    formData.append("locale", input.locale);
  }
  if (input.questionRef) {
    formData.append("questionRef", input.questionRef);
  }
  if (typeof input.attempt === "number") {
    formData.append("attempt", String(input.attempt));
  }
  if (typeof input.durationMs === "number") {
    formData.append("durationMs", String(input.durationMs));
  }

  const response = await fetch(
    `${API_BASE_URL}/forms/${encodeURIComponent(input.projectId)}/${encodeURIComponent(input.formId)}/${input.version}/voice/transcribe`,
    {
      method: "POST",
      headers: {
        "X-Session-Token": input.sessionToken,
      },
      body: formData,
    },
  );

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    throw new VoiceTranscriptionError(
      extractErrorMessage(payload) ??
        "Transcription failed. Retry or type manually?",
      extractErrorCode(payload),
    );
  }

  const data = unwrapApiResponse<VoiceTranscriptionResponse>(payload);
  if (!data) {
    throw new VoiceTranscriptionError(
      "Transcription failed. Retry or type manually?",
      "VOICE_TRANSCRIPTION_FAILED",
    );
  }

  return data;
}

